import { Platform, StyleSheet, View } from "react-native";
import React, { useEffect, useState } from "react";
import RacecardAndResultWidgetRunnerRow from "./RacecardAndResultWidgetRunnerRow";
import variables from "../../../../../styles/variables";
import {
  RacecardRes,
  ResultRes,
} from "../../../../../types/races/racecardAndResultWidget";
import { attachOddsToRunners } from "../../../../Screens/Tips/utils";
import RText from "../../../../RText";

/**
 * RacecardAndResultWidgetRunnerContainer is a shared component used across
 * the racecard and result widgets
 *
 * It displays a list of runners with their odds in either a single or double column layout
 * depending on whether it's a simple or detailed widget
 *
 * * If `isDetailed` is true, it shows up to six runners in two columns
 * * If `isDetailed` is false, it shows up to three runners in a single column
 *
 * @property {object} data - Object containing race information including runners
 * @property {boolean} isDetailed - Indicates if the widget should display in detailed mode (default: false)
 * @property {boolean} isRacecard - Indicates if the widget is a racecard widget (default: false),
 * otherwise it's a result widget
 *
 * @returns {JSX.Element} The RacecardAndResultWidgetRunnerContainer component
 */

type Props = {
  data: RacecardRes | ResultRes;
  isDetailed?: boolean;
  isRacecard?: boolean;
};

const RacecardAndResultWidgetRunnerContainer = ({
  data,
  isDetailed,
  isRacecard,
}: Props) => {
  const isResult = !isRacecard;
  const [runnersWithOdds, setRunnersWithOdds] = useState(data.runners);

  useEffect(() => {
    // Don't make call when results as we already have odds attached to
    // runners in the initial data object.
    if (isResult) {
      setRunnersWithOdds(data.runners);
      return;
    }

    //@ts-ignore - there are a million types that need to be updated if we update data to include RacecardRes | ResultRes
    attachOddsToRunners([data])
      .then((raceWithOdds) =>
        setRunnersWithOdds(raceWithOdds[0]?.runners || [])
      )
      .catch((e) => console.error("Error attaching odds:", e.message));
  }, [data]);

  const firstThreeRunners = runnersWithOdds.slice(0, 3);
  const secondThreeRunners = runnersWithOdds.slice(3, 6);

  const fullColumnStyle =
    firstThreeRunners.length === 3
      ? { justifyContent: "space-between" as const }
      : undefined;

  const fullSecondColumnStyle =
    secondThreeRunners.length === 3
      ? { justifyContent: "space-between" as const }
      : undefined;

  if (data?.status?.state === "abandoned")
    return (
      <RText
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
        weight="bold"
        color={variables.colors.palette.neutral.x500}
        uppercase
      >
        ABANDONED
      </RText>
    );

  return (
    <View
      style={[
        styles.mainContainer,
        isResult && {
          backgroundColor: variables.colors.palette.neutral.x50,
        },
        isDetailed
          ? { height: "100%" }
          : {
              paddingTop: 8,
              ...(Platform.OS == "web" ? { flex: 1 } : {}),
            },
      ]}
    >
      <View
        style={[
          styles.container,
          fullColumnStyle,
          isDetailed && { padding: 8 },
        ]}
      >
        {firstThreeRunners.map((runner) => (
          <RacecardAndResultWidgetRunnerRow
            key={runner.id}
            data={runner}
            isRacecard={isRacecard}
            isDetailedWidget={isDetailed}
          />
        ))}
      </View>
      {isDetailed && !!secondThreeRunners.length && (
        <>
          <View
            style={[
              styles.container,
              fullSecondColumnStyle,
              isDetailed && {
                padding: 8,
                borderLeftWidth: 1,
                borderLeftColor: variables.colors.palette.neutral.x200,
              },
            ]}
          >
            {secondThreeRunners.map((runner) => (
              <RacecardAndResultWidgetRunnerRow
                key={runner.id}
                data={runner}
                isRacecard={isRacecard}
                isDetailedWidget={isDetailed}
              />
            ))}
          </View>
        </>
      )}
    </View>
  );
};

export default RacecardAndResultWidgetRunnerContainer;

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: "row",
    borderRadius: 8,
  },
  container: {
    flex: 1,
    gap: 10,
    justifyContent: "space-between",
  },
  leftBorder: {
    borderLeftWidth: 1,
    borderLeftColor: variables.colors.palette.neutral.x200,
    marginHorizontal: 8,
  },
});
